import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings, Bike, Truck, Bus, Sparkles, Fuel, Zap, Leaf, Droplet, Battery, Wind, Shield } from 'lucide-react'
import { Accordion, InfoBox, IconOption } from '../../ui'
import { StepCard, FormGrid, FormField, SelectField, SelectOption, IconOptionField, FeatureGroup, Feature, CharacteristicsSelector } from '../../form'
import { Input } from '../../ui'
import { getVehicleFeatureGroupsForCategory } from '../../../hooks/useVehicleOptions'

interface VehicleRegistrationStep3SpecificationsProps {
  formData: {
    category: string
    fuelType: string
    transmission: string
    type: string
    doors: string
    steering: string
    color: string
    armorLevel?: string
    specialCategory?: string
    characteristics?: string[]
    // Motos
    motorcycleEngineType?: string
    motorcycleCoolingType?: string
    motorcycleStarterType?: string
    motorcycleFrontBrakeType?: string
    motorcycleRearBrakeType?: string
    motorcycleFeedingType?: string
    motorcycleStyle?: string
    motorcycleCategory?: string
    motorcycleCylinderCapacity?: string
    motorcycleGears?: string
    // Caminhões
    truckBodyType?: string
    truckCabType?: string
    truckAxles?: string
    truckTraction?: string
    truckGrossVehicleWeight?: string
    truckPayload?: string
    truckTires?: string
    // Ônibus
    busBodyBrand?: string
    busBodyModel?: string
    busOperationType?: string
    busFleetNumber?: string
    busSeatedCapacity?: string
    busStandingCapacity?: string
    busWheelchairCapacity?: string
    features: {
      comfort?: Record<string, boolean>
      safety?: Record<string, boolean>
      technology?: Record<string, boolean>
      convenience?: Record<string, boolean>
      performance?: Record<string, boolean>
      lighting?: Record<string, boolean>
      adas?: Record<string, boolean>
      busEquipment?: Record<string, boolean>
    }
  }
  errors: Record<string, string>
  vehicleOptions: any
  onInputChange: (field: string, value: any) => void
}

// Mapeamento de ícones de combustível
const fuelIconMap: Record<string, any> = {
  gasoline: Fuel,
  ethanol: Leaf,
  diesel: Droplet,
  flex: Zap,
  electric: Battery,
  hybrid: Wind,
}

const ELECTRIC_FUEL_TYPES = ['electric', 'hybrid']

export const VehicleRegistrationStep3Specifications: React.FC<VehicleRegistrationStep3SpecificationsProps> = ({
  formData,
  errors,
  vehicleOptions,
  onInputChange,
}) => {
  const { t } = useTranslation()
  const isCar = formData.category === 'B'
  const isMotorcycle = formData.category === 'A'
  const isTruck = formData.category === 'C'
  const isBus = formData.category === 'D'

  const featureGroups = useMemo(
    () => getVehicleFeatureGroupsForCategory(vehicleOptions, formData.category),
    [vehicleOptions, formData.category]
  )

  // Corrige transmissão automaticamente quando combustível é elétrico/híbrido
  useEffect(() => {
    if (ELECTRIC_FUEL_TYPES.includes(formData.fuelType) && formData.transmission !== 'automatic') {
      onInputChange('transmission', 'automatic')
    }
  }, [formData.fuelType])

  // Toggle de features
  const toggleFeature = (category: string, featureValue: string) => {
    const currentFeatures = formData.features[category as keyof typeof formData.features] || {}
    onInputChange('features', {
      ...formData.features,
      [category]: {
        ...currentFeatures,
        [featureValue]: !currentFeatures[featureValue],
      },
    })
  }

  // Converte fuel types para IconOption
  const fuelOptions: IconOption[] = vehicleOptions?.fuelTypes?.map((fuel: any) => ({
    value: fuel.value,
    label: fuel.label,
    icon: fuelIconMap[fuel.value] || Fuel,
  })) || []

  // Converte transmissions para IconOption — desabilita manual/automatedManual para elétrico/híbrido
  const transmissionOptions: IconOption[] = vehicleOptions?.transmissions?.map((trans: any) => ({
    value: trans.value,
    label: trans.label,
    icon: Settings,
  })) || []

  // Converte para SelectOption
  const bodyTypeOptions: SelectOption[] = vehicleOptions?.bodyTypes?.map((type: any) => ({
    value: type.value,
    label: type.label,
  })) || []

  const steeringOptions: SelectOption[] = vehicleOptions?.steeringTypes?.map((steering: any) => ({
    value: steering.value,
    label: steering.label,
  })) || []

  // Opções específicas de moto
  const motorcycleEngineOptions: SelectOption[] = vehicleOptions?.motorcycles?.engineTypes?.map((e: any) => ({
    value: e.value, label: e.label,
  })) || []
  const motorcycleCoolingOptions: SelectOption[] = vehicleOptions?.motorcycles?.coolingTypes?.map((c: any) => ({
    value: c.value, label: c.label,
  })) || []
  const motorcycleStarterOptions: SelectOption[] = vehicleOptions?.motorcycles?.starterTypes?.map((s: any) => ({
    value: s.value, label: s.label,
  })) || []
  const motorcycleBrakeOptions: SelectOption[] = vehicleOptions?.motorcycles?.brakeTypes?.map((b: any) => ({
    value: b.value, label: b.label,
  })) || []
  const motorcycleFeedingOptions: SelectOption[] = vehicleOptions?.motorcycles?.feedingTypes?.map((f: any) => ({
    value: f.value, label: f.label,
  })) || []
  const motorcycleStyleOptions: SelectOption[] = vehicleOptions?.motorcycles?.styles?.map((s: any) => ({
    value: s.value, label: s.label,
  })) || []
  const motorcycleCategoryOptions: SelectOption[] = vehicleOptions?.motorcycles?.categories?.map((c: any) => ({
    value: c.value, label: c.label,
  })) || []

  // Opções específicas de caminhão
  const truckBodyTypeOptions: SelectOption[] = vehicleOptions?.trucks?.bodyTypes?.map((o: any) => ({
    value: o.value, label: o.label,
  })) || []
  const truckCabTypeOptions: SelectOption[] = vehicleOptions?.trucks?.cabTypes?.map((o: any) => ({
    value: o.value, label: o.label,
  })) || []
  const truckTractionOptions: SelectOption[] = vehicleOptions?.trucks?.tractionTypes?.map((o: any) => ({
    value: o.value, label: o.label,
  })) || []

  // Opções específicas de ônibus
  const busBodyBrandOptions: SelectOption[] = vehicleOptions?.buses?.bodyBrands?.map((o: any) => ({
    value: o.value, label: o.label,
  })) || []
  const busOperationTypeOptions: SelectOption[] = vehicleOptions?.buses?.operationTypes?.map((o: any) => ({
    value: o.value, label: o.label,
  })) || []
  const busEquipmentFeatures: Feature[] = vehicleOptions?.buses?.equipment?.map((f: any) => ({
    value: f.value, label: f.label,
  })) || []

  // Converte features para Feature[] (listas variam por categoria A–D)
  const comfortFeatures: Feature[] = featureGroups.comfort?.map((f: any) => ({
    value: f.value,
    label: f.label,
  })) || []

  const safetyFeatures: Feature[] = featureGroups.safety?.map((f: any) => ({
    value: f.value,
    label: f.label,
  })) || []

  const technologyFeatures: Feature[] = featureGroups.technology?.map((f: any) => ({
    value: f.value,
    label: f.label,
  })) || []

  const convenienceFeatures: Feature[] = featureGroups.convenience?.map((f: any) => ({
    value: f.value,
    label: f.label,
  })) || []

  const performanceFeatures: Feature[] = featureGroups.performance?.map((f: any) => ({
    value: f.value,
    label: f.label,
  })) || []

  const lightingFeatures: Feature[] = featureGroups.lighting?.map((f: any) => ({
    value: f.value,
    label: f.label,
  })) || []

  const adasFeatures: Feature[] = featureGroups.adas?.map((f: any) => ({
    value: f.value,
    label: f.label,
  })) || []

  const isElectricOrHybrid = ELECTRIC_FUEL_TYPES.includes(formData.fuelType)

  return (
    <>
      {/* Especificações Técnicas - CARROS */}
      {isCar && (
        <StepCard title={t('vehicleRegistration.specifications.carTitle')} icon={Settings}>
          <IconOptionField
            label={t('vehicleRegistration.specifications.fuelLabel')}
            value={formData.fuelType}
            options={fuelOptions}
            onChange={(value) => onInputChange('fuelType', value)}
            name="fuelType"
            columns={5}
            required
            error={errors.fuelType}
            hint={formData.fuelType ? t('vehicleRegistration.specifications.formHint') : undefined}
          />
          <IconOptionField
            label={t('vehicleRegistration.specifications.transmissionLabel')}
            value={formData.transmission}
            options={transmissionOptions}
            onChange={(value) => onInputChange('transmission', value)}
            name="transmission"
            columns={5}
            required
            error={errors.transmission}
            hint={
              isElectricOrHybrid
                ? t('vehicleRegistration.specifications.automaticHint')
                : formData.transmission
                ? t('vehicleRegistration.specifications.formHint')
                : undefined
            }
          />

          <FormGrid columns={2}>
            <SelectField
              label={t('vehicleRegistration.specifications.bodyTypeLabel')}
              value={formData.type}
              options={bodyTypeOptions}
              onChange={(value) => onInputChange('type', value)}
              placeholder={t('vehicleRegistration.specifications.bodyTypePlaceholder')}
            />

            <FormField label={t('vehicleRegistration.specifications.doorsLabel')} error={errors.doors}>
              <Input
                type="number"
                value={formData.doors}
                onChange={(e) => onInputChange('doors', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.doorsPlaceholder')}
                min="2"
                max="5"
              />
            </FormField>

            <SelectField
              label={t('vehicleRegistration.specifications.steeringLabel')}
              value={formData.steering}
              options={steeringOptions}
              onChange={(value) => onInputChange('steering', value)}
              placeholder={t('vehicleRegistration.specifications.steeringPlaceholder')}
              hint={formData.steering ? t('vehicleRegistration.specifications.formHint') : undefined}
            />
          </FormGrid>
        </StepCard>
      )}

      {/* Especificações Técnicas - MOTOCICLETAS */}
      {isMotorcycle && (
        <StepCard title={t('vehicleRegistration.specifications.motorcycleTitle')} icon={Bike}>
          <IconOptionField
            label={t('vehicleRegistration.specifications.fuelLabel')}
            value={formData.fuelType}
            options={fuelOptions}
            onChange={(value) => onInputChange('fuelType', value)}
            name="fuelType"
            columns={5}
            required
            error={errors.fuelType}
            hint={formData.fuelType ? t('vehicleRegistration.specifications.formHint') : undefined}
          />
          <IconOptionField
            label={t('vehicleRegistration.specifications.transmissionLabel')}
            value={formData.transmission}
            options={transmissionOptions}
            onChange={(value) => onInputChange('transmission', value)}
            name="transmission"
            columns={5}
            required
            error={errors.transmission}
            hint={formData.transmission ? t('vehicleRegistration.specifications.formHint') : undefined}
          />

          <FormGrid columns={3}>
            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleEngineLabel')}
              value={formData.motorcycleEngineType || ''}
              options={motorcycleEngineOptions}
              onChange={(value) => onInputChange('motorcycleEngineType', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleCoolingLabel')}
              value={formData.motorcycleCoolingType || ''}
              options={motorcycleCoolingOptions}
              onChange={(value) => onInputChange('motorcycleCoolingType', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleStarterLabel')}
              value={formData.motorcycleStarterType || ''}
              options={motorcycleStarterOptions}
              onChange={(value) => onInputChange('motorcycleStarterType', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleFeedingLabel')}
              value={formData.motorcycleFeedingType || ''}
              options={motorcycleFeedingOptions}
              onChange={(value) => onInputChange('motorcycleFeedingType', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleFrontBrakeLabel')}
              value={formData.motorcycleFrontBrakeType || ''}
              options={motorcycleBrakeOptions}
              onChange={(value) => onInputChange('motorcycleFrontBrakeType', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleRearBrakeLabel')}
              value={formData.motorcycleRearBrakeType || ''}
              options={motorcycleBrakeOptions}
              onChange={(value) => onInputChange('motorcycleRearBrakeType', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />

            <FormField label={t('vehicleRegistration.specifications.motorcycleGearsLabel')}>
              <Input
                type="number"
                value={formData.motorcycleGears || ''}
                onChange={(e) => onInputChange('motorcycleGears', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.motorcycleGearsPlaceholder')}
                min="0"
                max="10"
              />
            </FormField>

            <FormField label={t('vehicleRegistration.specifications.motorcycleCylinderLabel')}>
              <Input
                type="number"
                value={formData.motorcycleCylinderCapacity || ''}
                onChange={(e) => onInputChange('motorcycleCylinderCapacity', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.motorcycleCylinderPlaceholder')}
                min="0"
              />
            </FormField>

            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleStyleLabel')}
              value={formData.motorcycleStyle || ''}
              options={motorcycleStyleOptions}
              onChange={(value) => onInputChange('motorcycleStyle', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.motorcycleCategoryLabel')}
              value={formData.motorcycleCategory || ''}
              options={motorcycleCategoryOptions}
              onChange={(value) => onInputChange('motorcycleCategory', value)}
              placeholder={t('vehicleRegistration.specifications.selectPlaceholder')}
            />
          </FormGrid>
        </StepCard>
      )}

      {/* Especificações Técnicas - CAMINHÕES */}
      {isTruck && (
        <StepCard title={t('vehicleRegistration.specifications.truckTitle')} icon={Truck}>
          <IconOptionField
            label={t('vehicleRegistration.specifications.fuelLabel')}
            value={formData.fuelType}
            options={fuelOptions}
            onChange={(value) => onInputChange('fuelType', value)}
            name="fuelType"
            columns={5}
            required
            error={errors.fuelType}
            hint={formData.fuelType ? t('vehicleRegistration.specifications.formHint') : undefined}
          />
          <IconOptionField
            label={t('vehicleRegistration.specifications.transmissionLabel')}
            value={formData.transmission}
            options={transmissionOptions}
            onChange={(value) => onInputChange('transmission', value)}
            name="transmission"
            columns={5}
            required
            error={errors.transmission}
            hint={formData.transmission ? t('vehicleRegistration.specifications.formHint') : undefined}
          />

          <FormGrid columns={3}>
            <SelectField
              label={t('vehicleRegistration.specifications.truckBodyTypeLabel')}
              value={formData.truckBodyType || ''}
              options={truckBodyTypeOptions}
              onChange={(value) => onInputChange('truckBodyType', value)}
              placeholder={t('vehicleRegistration.specifications.truckBodyTypePlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.truckCabTypeLabel')}
              value={formData.truckCabType || ''}
              options={truckCabTypeOptions}
              onChange={(value) => onInputChange('truckCabType', value)}
              placeholder={t('vehicleRegistration.specifications.truckCabTypePlaceholder')}
            />

            <SelectField
              label={t('vehicleRegistration.specifications.truckTractionLabel')}
              value={formData.truckTraction || ''}
              options={truckTractionOptions}
              onChange={(value) => onInputChange('truckTraction', value)}
              placeholder={t('vehicleRegistration.specifications.truckTractionPlaceholder')}
            />

            <FormField label={t('vehicleRegistration.specifications.truckAxlesLabel')} error={errors.truckAxles}>
              <Input
                type="number"
                value={formData.truckAxles || ''}
                onChange={(e) => onInputChange('truckAxles', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.truckAxlesPlaceholder')}
                min="2"
                max="10"
              />
            </FormField>

            <FormField label={t('vehicleRegistration.specifications.truckGrossVehicleWeightLabel')} error={errors.truckGrossVehicleWeight}>
              <Input
                type="number"
                value={formData.truckGrossVehicleWeight || ''}
                onChange={(e) => onInputChange('truckGrossVehicleWeight', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.truckGrossVehicleWeightPlaceholder')}
                min="0"
              />
            </FormField>

            <FormField label={t('vehicleRegistration.specifications.truckPayloadLabel')} error={errors.truckPayload}>
              <Input
                type="number"
                value={formData.truckPayload || ''}
                onChange={(e) => onInputChange('truckPayload', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.truckPayloadPlaceholder')}
                min="0"
              />
            </FormField>

            <SelectField
              label={t('vehicleRegistration.specifications.steeringLabel')}
              value={formData.steering}
              options={steeringOptions}
              onChange={(value) => onInputChange('steering', value)}
              placeholder={t('vehicleRegistration.specifications.steeringPlaceholder')}
              hint={formData.steering ? t('vehicleRegistration.specifications.formHint') : undefined}
            />

            <FormField label={t('vehicleRegistration.specifications.truckTiresLabel')}>
              <Input
                type="number"
                value={formData.truckTires || ''}
                onChange={(e) => onInputChange('truckTires', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.truckTiresPlaceholder')}
                min="4"
                max="22"
              />
            </FormField>
          </FormGrid>
        </StepCard>
      )}

      {/* Especificações Técnicas - ÔNIBUS */}
      {isBus && (
        <StepCard title={t('vehicleRegistration.specifications.busTitle')} icon={Bus}>
          <IconOptionField
            label={t('vehicleRegistration.specifications.fuelLabel')}
            value={formData.fuelType}
            options={fuelOptions}
            onChange={(value) => onInputChange('fuelType', value)}
            name="fuelType"
            columns={5}
            required
            error={errors.fuelType}
          />
          <IconOptionField
            label={t('vehicleRegistration.specifications.transmissionLabel')}
            value={formData.transmission}
            options={transmissionOptions}
            onChange={(value) => onInputChange('transmission', value)}
            name="transmission"
            columns={5}
            required
            error={errors.transmission}
          />

          <FormGrid columns={3}>
            <SelectField
              label={t('vehicleRegistration.specifications.busBodyBrandLabel')}
              value={formData.busBodyBrand || ''}
              options={busBodyBrandOptions}
              onChange={(value) => onInputChange('busBodyBrand', value)}
              placeholder={t('vehicleRegistration.specifications.busBodyBrandPlaceholder')}
            />

            <FormField label={t('vehicleRegistration.specifications.busBodyModelLabel')}>
              <Input
                value={formData.busBodyModel || ''}
                onChange={(e) => onInputChange('busBodyModel', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.busBodyModelPlaceholder')}
              />
            </FormField>

            <SelectField
              label={t('vehicleRegistration.specifications.busOperationTypeLabel')}
              value={formData.busOperationType || ''}
              options={busOperationTypeOptions}
              onChange={(value) => onInputChange('busOperationType', value)}
              placeholder={t('vehicleRegistration.specifications.busOperationTypePlaceholder')}
            />

            <FormField label={t('vehicleRegistration.specifications.busFleetNumberLabel')}>
              <Input
                value={formData.busFleetNumber || ''}
                onChange={(e) => onInputChange('busFleetNumber', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.busFleetNumberPlaceholder')}
              />
            </FormField>

            <FormField label={t('vehicleRegistration.specifications.busSeatedCapacityLabel')}>
              <Input
                type="number"
                value={formData.busSeatedCapacity || ''}
                onChange={(e) => onInputChange('busSeatedCapacity', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.busSeatedCapacityPlaceholder')}
                min="0"
              />
            </FormField>

            <FormField label={t('vehicleRegistration.specifications.busStandingCapacityLabel')}>
              <Input
                type="number"
                value={formData.busStandingCapacity || ''}
                onChange={(e) => onInputChange('busStandingCapacity', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.busStandingCapacityPlaceholder')}
                min="0"
              />
            </FormField>

            <FormField label={t('vehicleRegistration.specifications.busWheelchairCapacityLabel')}>
              <Input
                type="number"
                value={formData.busWheelchairCapacity || ''}
                onChange={(e) => onInputChange('busWheelchairCapacity', e.target.value)}
                placeholder={t('vehicleRegistration.specifications.busWheelchairCapacityPlaceholder')}
                min="0"
              />
            </FormField>
          </FormGrid>
        </StepCard>
      )}

      {/* Itens e Opcionais do Veículo */}
      <StepCard
        title={isBus ? t('vehicleRegistration.specifications.busFeaturesTitle') : t('vehicleRegistration.specifications.featuresTitle')}
        icon={Sparkles}
        description={t('vehicleRegistration.specifications.featuresDescription')}
      >
        <InfoBox variant="info">
          <strong>{t('vehicleRegistration.specifications.featuresTipTitle')}</strong> {t('vehicleRegistration.specifications.featuresTipMessage')}
        </InfoBox>

        <Accordion>
          {isBus && busEquipmentFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.busEquipmentGroupTitle')}
              features={busEquipmentFeatures}
              selectedFeatures={formData.features.busEquipment || {}}
              onToggle={(featureValue) => toggleFeature('busEquipment', featureValue)}
            />
          )}
          {!isBus && comfortFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.comfortGroupTitle')}
              features={comfortFeatures}
              selectedFeatures={formData.features.comfort || {}}
              onToggle={(featureValue) => toggleFeature('comfort', featureValue)}
            />
          )}

          {!isBus && safetyFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.safetyGroupTitle')}
              features={safetyFeatures}
              selectedFeatures={formData.features.safety || {}}
              onToggle={(featureValue) => toggleFeature('safety', featureValue)}
            />
          )}

          {!isBus && technologyFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.technologyGroupTitle')}
              features={technologyFeatures}
              selectedFeatures={formData.features.technology || {}}
              onToggle={(featureValue) => toggleFeature('technology', featureValue)}
            />
          )}

          {!isBus && convenienceFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.convenienceGroupTitle')}
              features={convenienceFeatures}
              selectedFeatures={formData.features.convenience || {}}
              onToggle={(featureValue) => toggleFeature('convenience', featureValue)}
            />
          )}

          {!isBus && performanceFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.performanceGroupTitle')}
              features={performanceFeatures}
              selectedFeatures={formData.features.performance || {}}
              onToggle={(featureValue) => toggleFeature('performance', featureValue)}
            />
          )}

          {!isBus && lightingFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.lightingGroupTitle')}
              features={lightingFeatures}
              selectedFeatures={formData.features.lighting || {}}
              onToggle={(featureValue) => toggleFeature('lighting', featureValue)}
            />
          )}

          {!isBus && adasFeatures.length > 0 && (
            <FeatureGroup
              title={t('vehicleRegistration.specifications.adasGroupTitle')}
              features={adasFeatures}
              selectedFeatures={formData.features.adas || {}}
              onToggle={(featureValue) => toggleFeature('adas', featureValue)}
            />
          )}
        </Accordion>
      </StepCard>

      {/* Blindagem e Categoria Especial — oculto para motos e ônibus */}
      {!isMotorcycle && !isBus && <StepCard
        title={t('vehicleRegistration.specifications.armorTitle')}
        icon={Shield}
        description={t('vehicleRegistration.specifications.armorDescription')}
      >
        <FormGrid columns={2}>
          <SelectField
            label={t('vehicleRegistration.specifications.armorLabel')}
            value={formData.armorLevel || ''}
            options={
              vehicleOptions?.armorLevels?.map((armor: any) => ({
                value: armor.value,
                label: armor.label,
              })) || []
            }
            onChange={(value) => onInputChange('armorLevel', value)}
            placeholder={t('vehicleRegistration.specifications.armorPlaceholder')}
          />

          <SelectField
            label={t('vehicleRegistration.specifications.specialCategoryLabel')}
            value={formData.specialCategory || ''}
            options={
              vehicleOptions?.specialCategories?.map((special: any) => ({
                value: special.value,
                label: special.label,
              })) || []
            }
            onChange={(value) => onInputChange('specialCategory', value)}
            placeholder={t('vehicleRegistration.specifications.specialCategoryPlaceholder')}
          />
        </FormGrid>

        <InfoBox variant="warning">
          <strong>{t('vehicleRegistration.specifications.armorOptionalTitle')}</strong> {t('vehicleRegistration.specifications.armorOptionalMessage')}
        </InfoBox>
      </StepCard>}

      {/* Outras Características */}
      {vehicleOptions?.characteristics?.length > 0 && (
        <StepCard
          title={t('vehicleRegistration.specifications.otherCharacteristicsTitle')}
          icon={Settings}
          description={t('vehicleRegistration.specifications.otherCharacteristicsDescription')}
        >
          <CharacteristicsSelector
            characteristics={vehicleOptions.characteristics}
            selectedValues={formData.characteristics || []}
            onChange={(values) => onInputChange('characteristics', values)}
          />

          <InfoBox variant="info">
            <strong>{t('vehicleRegistration.specifications.armorOptionalTitle')}</strong> {t('vehicleRegistration.specifications.otherCharacteristicsOptionalMessage')}
          </InfoBox>
        </StepCard>
      )}
    </>
  )
}
